import { Divider, Tab, Tabs, Theme, useMediaQuery } from "@material-ui/core";
import Work from '@material-ui/icons/Work';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { AutocompleteInput, BooleanInput, Create, Datagrid, Edit, FileInput, List, NumberInput, ReferenceField, ReferenceInput, required, SelectInput, SimpleForm, TextField, TextInput, useAuthProvider, useDataProvider, useRecordContext, useRedirect, useTranslate } from 'react-admin';
import { Route } from 'react-router';
import { Column, CustomTitle, DefaultToolbar, doChoices, GCloneButton, Legend, Loader, Row, TranslateField } from '../common/components';
import { filterObject, filterValue, isCreate } from '../common/components/utils/fn';
import { Project, projectValidate } from "../services/project/actions";
import { LotList } from './Lot';
import { ProjectStages } from './ProjectStages';
import { WorkflowDisplay } from './Workflow';
import { ProjectDelegateList } from "./ProjectDelegate";
import { useForm } from 'react-final-form';
import { ProjectMap } from "./ProjectMap";
import { ProjectStats } from "./ProjectStats";

export const ProjectIcon = Work;

export const ProjectList = props => {
    const authProvider = useAuthProvider();
    const [security, setSecurity] = useState(null);
    useEffect(() => {
        const id = authProvider.getId();
        const role = authProvider.getRole();
        if (role === "projectManager") {
            setSecurity({
                _or: filterValue([
                    filterObject("ownerId._eq", id),
                    filterObject("project_delegates.userId._eq", id)
                ])
            });
        }
    }, [authProvider]);
    if (!authProvider) <Loader />;
    const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
    return (
        <List {...props} bulkActionButtons={false} filter={security} filters={[
            <TextInput source="name" alwaysOn />,
            <TextInput source="description" />
        ]}>
            <Datagrid rowClick="edit" >
                {isSmall ? <Column source="name" noClone>
                    <TextField source="name" />
                    <TextField className="secondary" source="description" />
                </Column>
                    : <TextField source="name" />}
                {!isSmall &&
                    <TextField source="description" />}
                <TranslateField source="status" />
                {!isSmall &&
                    <ReferenceField source="workflowId" reference="workflow">
                        <TextField source="name" />
                    </ReferenceField>
                }
                <GCloneButton />
            </Datagrid>
        </List>
    );
};

const DashboardInput = () => {
    const form = useForm();
    const values = form.getState().values;
    return <>{
        values.hasCustomDashboard === true && <NumberInput step={1} source="customDashboardId" validate={[required()]} />
    }</>
};

const ProjectDetailsForm = (props) => {
    const authProvider = useAuthProvider();
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const isEdit = !isCreate();
    const record: Project = useRecordContext();
    const isNotDraft = record.status && record.status !== "draft";
    const hasMap = record?.hasMap === true;
    const ownerId = authProvider?.getId();
    if (!ownerId) return <Loader />;
    return <SimpleForm {...props} initialValues={{ ownerId, hasCustomDashboard: false, hasExternalProductionFile: false }} label="resources.details" toolbar={<DefaultToolbar hasSave onActivate={projectValidate(dataProvider, translate, record)} />}>
        {!isEdit && <Legend />}
        <Row>
            <TextInput disabled={isNotDraft} source="name" validate={[required()]} />
            <TextInput disabled={isNotDraft} source="atheneo" />
            <SelectInput disabled source="status" choices={doChoices.status} defaultValue="draft" />
        </Row>
        <TextInput disabled={isNotDraft} source="description" multiline />
        <Row>
            <TextInput disabled={isNotDraft} source="unitProd" validate={[required()]} />
            <TextInput disabled={isNotDraft} source="unitMeasure" validate={[required()]} />
        </Row>
        <ReferenceInput source="workflowId" reference="workflow" filter={{ status: "enabled" }} validate={[required()]}>
            <AutocompleteInput disabled={isEdit} optionText={<WorkflowDisplay />} inputText={(r) => r.name} />
        </ReferenceInput>
        <ReferenceInput disabled source="ownerId" reference="user" validate={[required()]}>
            <SelectInput optionText="fullname" />
        </ReferenceInput>
        <BooleanInput source="hasExternalProductionFile" fullWidth helperText={translate("resources.project.fields.hasExternalProductionFileHelper")} />
        <Row>
            <BooleanInput source="hasCustomDashboard" />
            <DashboardInput />
        </Row>
        <BooleanInput source="hasMap" />
    </SimpleForm>
}

const getTabs = (hasDelegate:boolean, hasMap:boolean) => {
    let tabs = [];
    tabs.push({ id: "details", label: "resources.details" });
    tabs.push({ id: "stage", label: "resources.project.stages.name" });
    if (hasDelegate) {
        tabs.push({ id: "delegate", label: "resources.project.delegate.name" });
    }
    tabs.push({ id: "lot", label: "resources.project.lot.name" });
    tabs.push({ id: "stats", label: "resources.project.stats.name" });
    if (hasMap) {
        tabs.push({ id: "map", label: "resources.project.map.name" });
    }
    return tabs;
};

const ProjectEditForm = (props) => {
    const authProvider = useAuthProvider();
    const redirect = useRedirect();
    const record = useRecordContext();
    const id = record?.id;
    const path = `/project/${id}`;
    const handleChange = useCallback((event: React.ChangeEvent<{}>, value: string) => {
        const v = (value === "details" ? path : `${path}/${value}`)
        redirect(v);
    }, [path, redirect]);
    const translate = useTranslate();
    const userId = authProvider?.getId();
    const role = authProvider?.getRole();
    const hasDelegate = userId === record?.ownerId || role === "admin";
    const hasMap = record?.hasMap === true;
    if (!userId) return <Loader />;
    return <Fragment>
        <Tabs value={props.tab} onChange={handleChange}>
            {getTabs(hasDelegate, hasMap).map(choice => <Tab key={choice.id} label={translate(choice.label)} value={choice.id} />)}
        </Tabs>
        <Divider />
        <Route exact path="/project/:id">
            <ProjectDetailsForm {...props} />
        </Route>
        <Route path="/project/:id/stage">
            <ProjectStages {...props} className="padding" />
        </Route>
        {hasDelegate && <Route path="/project/:id/delegate">
            <ProjectDelegateList className="padding" projectId={id} />
        </Route>
        }
        <Route exact path="/project/:id/lot">
            <LotList projectId={id} projectStatus={record?.status} hasMap={record?.hasMap} className="padding" />
        </Route>
        <Route exact path="/project/:id/stats">
            <ProjectStats id={id} customDashboardId={record?.customDashboardId} hasCustomDashboard={record?.hasCustomDashboard} />
        </Route>
        <Route exact path="/project/:id/map">
            <ProjectMap id={id} />
        </Route>
    </Fragment>
}

export const ProjectEdit = ({ tab = "details", ...props }) =>
    <Edit {...props} title={<CustomTitle title="project" />}>
        <ProjectEditForm tab={tab} />
    </Edit >;

export const ProjectCreate = props =>
    <Create {...props}>
        <ProjectDetailsForm />
    </Create>
